/* eslint-disable max-lines-per-function */
import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
// @ts-ignore
import { FormControlLabel, Switch, Typography, Grid } from "@mui/material";
import { FormikConfig } from "formik";
import { getPropertyById, updateProperty, updatePropertyVariables } from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { CCEmailForm } from "./cc-email-form";

const SUCCESS_MESSAGE = "Success";
const INVALID_EMAIL_MESSAGE = "Invalid Email Format";
const ERROR_MESSAGE = "Error updating email, please support@havenconnect.com for assistance.";

const PROPERTY = gql`
  query getPropertyById($id: ID!) {
    property(id: $id) {
      id
      name
      phone
      email
      respondToEmail
      respondToEmailEnabled
    }
  }
`;

const UPDATE_PROPERTY = gql`
  mutation updateProperty($id: ID!, $data: PropertyUpdateInput!) {
    updateProperty(id: $id, data: $data) {
      id
      respondToEmail
      respondToEmailEnabled
    }
  }
`;

type Props = {
  propertyId: string;
}

export const RespondToEmailForm : React.FC<Props> = ({ propertyId }) => {
  const { openSnackbar } = useSnackbar();

  const [updatePropertySettings] =
    useMutation<updateProperty, updatePropertyVariables>(UPDATE_PROPERTY);
  const { data, loading, error } = useQuery<getPropertyById>(PROPERTY, {
    fetchPolicy: "cache-first",
    variables: { id: propertyId },
  });

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;
  const { property } = data;

  const enabled = property.respondToEmailEnabled;

  const toggleEnabled = (isEnabled: boolean) => {
    if (!property.respondToEmail && isEnabled) {
      openSnackbar({ message: INVALID_EMAIL_MESSAGE, status: "error" });
      return;
    }

    updatePropertySettings({
      variables: {
        id: property.id,
        data: {
          respondToEmailEnabled: isEnabled,
        },
      },
    })
      .then(() => {
        openSnackbar({ message: SUCCESS_MESSAGE, status: "success" });
      });
  };

  const updateEmail: FormikConfig<{email: string}>["onSubmit"] = (values) => {
    const isEnabled = !!values.email && enabled;

    updatePropertySettings({
      variables: {
        id: property.id,
        data: {
          respondToEmail: values.email,
          respondToEmailEnabled: isEnabled,
        },
      },
    })
      .then(() => {
        openSnackbar({ message: SUCCESS_MESSAGE, status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: ERROR_MESSAGE, status: "error", duration: 4000 });
      });
  };

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6">Respond to</Typography>
        <Typography variant="subtitle2">Define the email address for this property.</Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <CCEmailForm initialEmail={property.respondToEmail ?? ""} onSubmit={updateEmail} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          style={{ marginLeft: 0 }}
          labelPlacement="start"
          label="Enable for all users at this property: "
          control={<div style={{ paddingLeft: 8 }}>
            <Switch
              onClick={() => toggleEnabled(!enabled)}
              checked={enabled}
            /></div>}
        />
      </Grid>
    </>
  );
};
